/*

A Date Picker is a field of links that end in an ISO date, with some navigation, presented in a popup.

There are 4 props;
  earliestDate: the earliest date that can be selected. Optional, and defaults to "2005-01-01"
  latestDate: the latest date that can be selected. Optional, and defaults to today
  date: the initial date that the is selected in the popup
  urlBase: the base on which all the Link hrefs are based

At first implementation, this is only used on the Horse racing full results list. Where easy it has been coded to be generic, but this is not universal.

*/
import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { Link } from 'react-router';
import CalendarIcon from 'modules/Common/components/svg/CalendarIcon';
import CaretIcon from 'modules/Common/components/svg/CaretIcon';
import { preventLinkScroll } from 'utils/commonUtils';
import { isoDate, momentInUTC } from 'utils/LocalDateTimeHelper';

require("./DatePicker.scss");

const earliestDataDate = "2005-01-01";

export default class DatePicker extends Component {
  static propTypes = {
    earliestDate: PropTypes.string,
    latestDate: PropTypes.string,
    date: PropTypes.string,
    urlBase: PropTypes.string,
    children: PropTypes.node
  }

  constructor(...args) {
    super(...args);

    this.todayIso = isoDate(new Date());

    this.state = this.initState(this.props);
    this.removeDocumentClickToClose = () => {}; // initial noop, gets reassigned by addDocumentClickToClose
  }

  initState(props) {
    const {
      date,
      earliestDate = earliestDataDate,
      latestDate = this.todayIso
    } = props;

    this.initialDate = date;
    this.selectedDate = new Date(date);
    this.selectedDate.setDate(1); // prevent issues with days that aren't in every month (eg 31st) and this is only to get the month

    this.earliestDate = new Date(earliestDate);
    this.latestDate = new Date(latestDate);

    return {
      popupOpen: false,
      ...this.getStateForSelectedDate()
    };
  }

  togglePopup(isOpen = !this.state.popupOpen) {
    this.setState({
      popupOpen: isOpen
    }, () => {
      if (isOpen) {
        this.addDocumentClickToClose();
      }
      else {
        this.removeDocumentClickToClose();
      }
    });
  }

  addDocumentClickToClose() {
    this.removeDocumentClickToClose(); // remove any existing - there can be only one

    const closePopup = e => { // unique new function for this time
      if (!(this.popupDomElement && this.popupDomElement.contains(e.target))) {
        this.togglePopup(false);
      }
    }

    document.addEventListener("click", closePopup, false); // add it

    this.removeDocumentClickToClose = () => { // store a function to remove it
      document.removeEventListener("click", closePopup);
      this.removeDocumentClickToClose = () => {}; // reset self
    }
  }

  getStateForSelectedDate() {
    return {
      displayDate: new Date(this.selectedDate),
      currentMonth: this.getDisplayRange()
    };
  }

  moveMonth(by) {
    this.selectedDate.setMonth(this.selectedDate.getMonth() + by);
    this.setState(this.getStateForSelectedDate());
  }

  moveYear(by) {
    this.selectedDate.setFullYear(this.selectedDate.getFullYear() + by);
    this.setState(this.getStateForSelectedDate());
  }

  inRange(testDate) {
    return (testDate <= this.latestDate) && (testDate >= this.earliestDate);
  }

  cantMoveMonth(by) {
    const testDate = new Date(this.selectedDate);
    testDate.setMonth(testDate.getMonth() + by);
    return !this.inRange(testDate);
  }

  cantMoveYear(by) {
    const testDate = new Date(this.selectedDate);
    testDate.setFullYear(testDate.getFullYear() + by);
    return !this.inRange(testDate);
  }

  getDisplayRange() {
    // this.selectedDate is always the 1st of the month, because the constructor sets it
    const start = new Date(this.selectedDate);

    // back to Monday
    start.setDate(2 - (start.getDay() || 7));

    const end = new Date(this.selectedDate);

    // last day of the month
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(1,0,0,0);

    // forward to Sunday
    end.setDate(end.getDate() + 7 - (end.getDay() || 7));

    const range = [];

    const current = new Date(start);
    current.setHours(1,0,0,0);

    while ((current - end) <= (2 * 60 * 60 * 1000)) { // can get confused by DST change, so allow a margin
      range.push({
        src: new Date(current),
        date: isoDate(current),
        label: current.getDate(),
        inMonth: current.getMonth() == this.selectedDate.getMonth(),
        inRange: this.inRange(current)
      });

      current.setDate(current.getDate() + 1);
    }

    return range;
  }

  componentWillUnmount() {
    this.removeDocumentClickToClose();
  }

  componentWillReceiveProps(newProps) {
    if (newProps.date != this.props.date) {
      this.setState(this.initState(newProps));
    }
  }

  getDomElements(el) {
    if (el) {
      this.popupDomElement = el;
    }
  }

  render() {
    const {urlBase, children} = this.props;

    const momentDisplay = momentInUTC(this.state.displayDate);
    const monthLabel = momentDisplay.format("MMMM");
    const yearLabel = momentDisplay.format("YYYY");

    return <nav className="date-picker">
      <button className="date-picker-icon-button" onClick={() => this.togglePopup()}>
        <CalendarIcon/>
      </button>
      <div className={`date-picker-popup${this.state.popupOpen?"-open":""}`} ref={el => this.getDomElements(el)}>
        <div className="date-picker-heading">
          <div className="date-picker-caret"><button disabled={this.cantMoveMonth(-1)} onClick={() => this.moveMonth(-1)}><CaretIcon direction="left" type="line"/></button></div>
          <div className="date-picker-month-label">{monthLabel}</div>
          <div className="date-picker-caret"><button disabled={this.cantMoveMonth(1)} onClick={() => this.moveMonth(1)}><CaretIcon direction="right" type="line"/></button></div>
          <div className="date-picker-caret"><button disabled={this.cantMoveYear(-1)} onClick={() => this.moveYear(-1)}><CaretIcon direction="left" type="line"/></button></div>
          <div className="date-picker-year-label">{yearLabel}</div>
          <div className="date-picker-caret"><button disabled={this.cantMoveYear(1)} onClick={() => this.moveYear(1)}><CaretIcon direction="right" type="line"/></button></div>
        </div>
        <div className="date-picker-row">
          <div className="date-picker-label">Mon</div>
          <div className="date-picker-label">Tue</div>
          <div className="date-picker-label">Wed</div>
          <div className="date-picker-label">Thu</div>
          <div className="date-picker-label">Fri</div>
          <div className="date-picker-label">Sat</div>
          <div className="date-picker-label">Sun</div>
        </div>
        <div className="date-picker-row">
          {this.state.currentMonth.map(day => {
            let href, className;
            const key = day.date;

            if (this.todayIso == day.date) {
              href = urlBase;
            }
            else {
              href = `${urlBase}/${day.date}`;
            }

            if (day.inMonth) {
              className = "date-picker-in-month";
            }
            else {
              className = "date-picker-ex-month";
            }

            if (this.initialDate == day.date) {
              className = `${className} date-picker-initial`;
            }

            if (day.inRange) {
              return <Link {...{key, className}} to={preventLinkScroll(href)} onClick={() => this.togglePopup(false)}>{day.label}</Link>
            }
            else {
              return <span {...{key, className}}>{day.label}</span>
            }
          })}
        </div>
        {children}
      </div>
    </nav>;
  }
}



// WEBPACK FOOTER //
// ./modules/Common/components/DatePicker.js